import React, {useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import styled from 'styled-components';
import {Detail, Heading, Icon, Tabs} from '@forecast-it/design-system';
import {encodeCompanyId} from '../featureFlagRows';
import {FEATURE_FLAG_LINK_TEXT} from '../featureFlagSurface';
import {RolloutDirectory} from '../rollout/featureFlagRolloutDirectory';
import {AlwaysLastTab} from './AlwaysLastTab';
import {BetaProgramTab} from './BetaProgramTab';
import {CohortsTab} from './CohortsTab';
import {buildCohortViews, buildMembershipIndex} from './featureFlagLists';

const isPresent = <T,>(value: T | null | undefined): value is T => value != null;

const ScreenWrapper = styled.div`
	max-width: 1400px;
	margin: 0 auto;
	padding: 20px 32px 64px;
`;

const BackLink = styled(Link)`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 14px;
	text-decoration: none;

	/* The typography components paint their own colour onto the element they render, so a colour
	   inherited from this link would lose to it and has to be set on the children instead. The arrow
	   takes its colour from its own prop and is unaffected. */
	> * {
		color: ${FEATURE_FLAG_LINK_TEXT};
	}

	&:hover {
		text-decoration: underline;
	}
`;

const HeaderBlock = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	margin-bottom: 20px;
`;

const COHORTS_TAB = 'cohorts';
const ALWAYS_LAST_TAB = 'alwaysLast';
const BETA_TAB = 'beta';

const SUBTITLE =
	'Cohorts and the always-last list are shared across every feature flag — changes here affect future rollouts, not companies already assigned.';

/** Cohort shape the screen needs from the page fragment — a structural subset of the Relay query result. */
export interface FeatureFlagListsCohortSource {
	readonly id: string | null;
	readonly cohortId: number | null;
	readonly name: string | null;
	readonly companyIds: ReadonlyArray<number | null> | null;
}

/** The `featureFlagLists` shape the screen needs — a structural subset of the Relay query result. */
export interface FeatureFlagListsSource {
	readonly cohorts: ReadonlyArray<FeatureFlagListsCohortSource | null> | null;
	readonly alwaysLastCompanyIds: ReadonlyArray<number | null> | null;
}

export interface FeatureFlagListsScreenProps {
	/** Where the flag list lives. Owned by the page, which holds the route. */
	backPath: string;
	lists: FeatureFlagListsSource | null | undefined;
	directory: RolloutDirectory;
	/** Encoded company ids currently opted into the beta programme. */
	betaCompanyIds: readonly string[];
}

/**
 * The admin screen for the two flag-independent lists — cohorts and the always-last list — plus a
 * read-only view of the beta programme. All three tabs share one membership index, built once here so
 * each tab's "also in" note can never disagree about what a company belongs to.
 */
export function FeatureFlagListsScreen({backPath, lists, directory, betaCompanyIds}: FeatureFlagListsScreenProps): JSX.Element {
	const [tab, setTab] = useState<string>(COHORTS_TAB);

	const cohorts = useMemo(() => buildCohortViews(lists?.cohorts), [lists]);
	const alwaysLastCompanyIds = useMemo(
		() => (lists?.alwaysLastCompanyIds ?? []).filter(isPresent).map(encodeCompanyId),
		[lists]
	);
	const index = useMemo(
		() => buildMembershipIndex({cohorts, alwaysLastCompanyIds, betaCompanyIds}),
		[cohorts, alwaysLastCompanyIds, betaCompanyIds]
	);

	return (
		<ScreenWrapper>
			<BackLink data-cy="feature-flag-lists-back" to={backPath}>
				<Icon size="s" icon="arrowLeft" newColor="primary" />
				<Detail>All feature flags</Detail>
			</BackLink>
			<HeaderBlock>
				<Heading size="3">Manage lists</Heading>
				<Detail color="medium">{SUBTITLE}</Detail>
			</HeaderBlock>
			<Tabs value={tab} onValueChange={setTab}>
				<Tabs.List>
					<Tabs.Tab value={COHORTS_TAB} data-cy="feature-flag-lists-tab-cohorts">
						Cohorts
					</Tabs.Tab>
					<Tabs.Tab value={ALWAYS_LAST_TAB} data-cy="feature-flag-lists-tab-always-last">
						Always last
					</Tabs.Tab>
					<Tabs.Tab value={BETA_TAB} data-cy="feature-flag-lists-tab-beta">
						Beta program
					</Tabs.Tab>
				</Tabs.List>
				<Tabs.Content value={COHORTS_TAB}>
					<CohortsTab cohorts={cohorts} index={index} directory={directory} />
				</Tabs.Content>
				<Tabs.Content value={ALWAYS_LAST_TAB}>
					<AlwaysLastTab alwaysLastCompanyIds={alwaysLastCompanyIds} index={index} directory={directory} />
				</Tabs.Content>
				<Tabs.Content value={BETA_TAB}>
					<BetaProgramTab betaCompanyIds={betaCompanyIds} index={index} directory={directory} />
				</Tabs.Content>
			</Tabs>
		</ScreenWrapper>
	);
}
